import { AmountOfPaymentGreaterThanDocumentValueError, CompanyParametersQuery, CurrencyCollectionQuery, OnePaymentOnCashBasisDocumentError, PaymentMethodQuery, propertyChangedFilter, propertyChangedJoinPointFilter, splitPaymentChecked } from '@skylab-shared/business';
import { BusinessObjectPart, BusinessObjectPartContext, ModelBase, ObservableCollection, PropertyChangedJoinPoint, Weaver } from '@skylab-shared/core';
import { DocumentPaymentListItemModel, SplitPaymentState } from '@skylab-shared/dataModel';
import { Constructor } from '@skylab-shared/types';
import { Calc, MidpointRounding, addDays, dateDiffInDays, withFlag } from '@skylab-shared/utils';
import { PurchaseDueDateEarlierThanIssueDateError } from '../violations/purchase-due-date-earlier-than-issue-date-error';
import { PurchaseOnePaymentOnCurrencyDocumentError } from '../violations/purchase-one-payment-on-currency-document-error';
import { PurchaseWrongPaymentMethodOnDocumentWithSplitPaymentWarning } from '../violations/purchase-wrong-payment-method-on-document-with-split-payment-warning';

interface PurchaseModelPart extends ModelBase {
    issueDate: Date;
    vatDeductionDate: Date;
    dueDate: Date;
    receiptDate: Date;
    paymentMethodId: number;
    grossValue: number;
    dueDateDays: number;
    splitPayment: SplitPaymentState;
    payments: ObservableCollection<DocumentPaymentListItemModel>;
    cashBasis: boolean;
    issuedByVatPayer: boolean;
    currencyId: number;
}

export class PurchaseDocumentPaymentsBOPart extends BusinessObjectPart<PurchaseModelPart> {
    public constructor(
        weaver: Weaver
    ) {
        super(weaver);
    }
    public override async initializeAsync(context: BusinessObjectPartContext<PurchaseModelPart>): Promise<void> {
        await super.initializeAsync(context);
        this.registerPropertyChangesRules();
    }

    private registerPropertyChangesRules(): void {
        this.registerDueDateEarlierThanIssueDateRule();
        this.setDueDateAccordingToPaymentMethod();
        this.setDueDateAccordingToIssueDateDueDateInDays();
        this.registerWrongPaymentMethodWithSplitPayment();
        this.registerAmountOfPaymentsValidationRule();
        this.registerOnePaymentOnCashBasisDocumentValidationRule();
        this.registerOnePaymentOnCurrencyDocument();
    }

    private registerOnePaymentOnCurrencyDocument(): void {
        this.engine.validation.addRule(
            [[DocumentPaymentListItemModel, 'value']],
            ctx => {
                const { systemCurrency } = this.bo.localContext.getPreloaded(CompanyParametersQuery)!;
                const baseCurrency = this.bo.localContext.firstPreloaded(CurrencyCollectionQuery, { symbol: systemCurrency });
                if (baseCurrency?.id === this.bo.model.currencyId) return true;

                const atMostOnePayment = ctx.bo.model.payments.length <= 1;
                const paymentItemValue = ctx.source.value;
                return atMostOnePayment && Math.abs(paymentItemValue) === Math.abs(ctx.bo.model.grossValue);
            },
            PurchaseOnePaymentOnCurrencyDocumentError
        );
    }

    private registerOnePaymentOnCashBasisDocumentValidationRule(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this.bo)(this.modelType, ['grossValue', 'cashBasis'])(
            () => this.engine.validation.queueValidation([DocumentPaymentListItemModel, 'value'])
        )));
        this.engine.validation.addRule(
            [[DocumentPaymentListItemModel, 'value']],
            ctx => {
                if (!ctx.bo.model.cashBasis)
                    return true;
                const atMostOnePayment = ctx.bo.model.payments.length <= 1;
                const paymentItemValue = ctx.source.value;
                return atMostOnePayment && Math.abs(paymentItemValue) === Math.abs(ctx.bo.model.grossValue);
            },
            OnePaymentOnCashBasisDocumentError
        );
    }

    private registerAmountOfPaymentsValidationRule(): void {
        const getPaymentsSum = (payments: ObservableCollection<DocumentPaymentListItemModel>) => {
            const { systemCurrency } = this.bo.localContext.getPreloaded(CompanyParametersQuery)!;
            const baseCurrency = this.bo.localContext.firstPreloaded(CurrencyCollectionQuery, { symbol: systemCurrency })!;
            return Calc.round(payments.reduce((sum, payment) => sum + payment.value, 0), baseCurrency.precision, MidpointRounding.AwayFromZero);
        };

        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this.bo)(DocumentPaymentListItemModel, ['value'])(
            () => this.engine.validation.queueValidation([DocumentPaymentListItemModel, 'value'])
        )));

        this.engine.validation.addRule(
            [[this.modelType, 'grossValue']],
            [[DocumentPaymentListItemModel, 'value']],
            ctx => {
                const paymentsSum = getPaymentsSum(ctx.bo.model.payments);
                const invalidForPositiveValue = this.bo.model.grossValue >= 0 && paymentsSum > this.bo.model.grossValue;
                const invalidForNegativeValue = this.bo.model.grossValue < 0 && paymentsSum > Math.abs(this.bo.model.grossValue);
                return !(invalidForPositiveValue || invalidForNegativeValue);
            },
            AmountOfPaymentGreaterThanDocumentValueError
        );
    }

    private registerWrongPaymentMethodWithSplitPayment(): void {
        this.engine.validation.addRule(
            [[this.modelType, 'splitPayment'], [this.modelType, 'paymentMethodId']],
            [[this.modelType, 'paymentMethodId']],
            ctx => {
                const bankTransferIds = this.bo.localContext.filterPreloaded(PaymentMethodQuery, {
                    cashPayment: false,
                    associatedWithBankAccount: true
                }).map(({ id }) => id);
                return !(splitPaymentChecked(ctx.source.splitPayment) && !bankTransferIds.includes(ctx.source.paymentMethodId));
            },
            PurchaseWrongPaymentMethodOnDocumentWithSplitPaymentWarning
        );
    }

    private registerDueDateEarlierThanIssueDateRule(): void {
        this.engine.validation.addRule(
            [[this.modelType, 'issueDate'], [this.modelType, 'dueDate']],
            [[this.modelType, 'dueDate']],
            ctx => {
                if (!ctx.source.dueDate || !ctx.source.issueDate) {
                    return true;
                }

                return dateDiffInDays(ctx.source.dueDate, ctx.source.issueDate) >= 0;
            },
            PurchaseDueDateEarlierThanIssueDateError
        );
    }

    private setDueDateAccordingToPaymentMethod(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this.bo)(this.modelType, ['paymentMethodId'])(
            () => {
                const paymentMethod = this.bo.localContext.firstPreloaded(PaymentMethodQuery, { id: this.bo.model.paymentMethodId });
                if (!paymentMethod) return;
                this.bo.model.dueDateDays = paymentMethod.dueDate;
            }
        )));
    }

    private setDueDateAccordingToIssueDateDueDateInDays(): void {
        const modelType = <Constructor<PurchaseModelPart>> this.bo.model.constructor;

        this.coreFeature.propertyChanged.listen<PurchaseModelPart>(propertyChangedFilter(
            modelType,
            ['dueDate', 'dueDateDays', 'issueDate'],
            ctx => {
                withFlag(() => {
                    this.calculateDueDate(ctx.model, ctx.propertyName);
                }, 'due-date-calculation');
            }
        ));
    }

    private calculateDueDate(document: PurchaseModelPart, updatedProperty: keyof PurchaseModelPart): void {
        if (updatedProperty === 'dueDate') {
            document.dueDateDays = dateDiffInDays(document.dueDate, document.issueDate);
        } else if (['dueDateDays', 'issueDate'].includes(updatedProperty) && this.bo.model.issueDate) {
            document.dueDate = addDays(document.issueDate, document.dueDateDays);
        }
    }
}
